import { rashiNames } from '@ishubhamx/panchangam-js';
import { VARNA_BY_MOON_SIGN_ELEMENT } from './jyotish_local';

/**
 * Varna Profile — an interpretive model of this app, NOT a classical Jyotish formula.
 * Classical Varna Kuta (Ashtakoota) uses Moon Rashi only. Roles in the profile:
 * Moon D1 — core nature, Moon D9 — inner potential, Ascendant — outward expression, Sun — personal will.
 * @see https://saravali.github.io/astrology/koota_varna.html
 *
 * Varna names are internal ids only: the UI shows the masked theme (uiKey → jyotish_ui.json).
 */

export const VARNA_ORDER = ['brahmin', 'kshatriya', 'vaishya', 'shudra'];

export const VARNA = {
  brahmin: {
    id: 'brahmin',
    uiKey: 'j_b',
    title: { en: 'Brahmin', ru: 'Брахман' },
    theme: { en: 'Knowledge & Meaning', ru: 'Понимание и Смысл' },
  },
  kshatriya: {
    id: 'kshatriya',
    uiKey: 'j_k',
    title: { en: 'Kshatriya', ru: 'Кшатрий' },
    theme: { en: 'Action & Leadership', ru: 'Действие и Лидерство' },
  },
  vaishya: {
    id: 'vaishya',
    uiKey: 'j_v',
    title: { en: 'Vaishya', ru: 'Вайшья' },
    theme: { en: 'Growth & Exchange', ru: 'Рост и Взаимодействие' },
  },
  shudra: {
    id: 'shudra',
    uiKey: 'j_s',
    title: { en: 'Shudra', ru: 'Шудра' },
    theme: { en: 'Craft & Service', ru: 'Дело и Мастерство' },
  },
};

/** Saravali element rule: Water → Brahmin, Fire → Kshatriya, Air → Vaishya, Earth → Shudra. */
export const VARNA_BY_RASHI = Object.freeze(
  Object.fromEntries(
    Object.entries(VARNA_BY_MOON_SIGN_ELEMENT).flatMap(([varnaId, entry]) =>
      Object.values(entry.moonSigns).map((sign) => [sign, varnaId])
    )
  )
);

export const getVarnaByRashi = (sign) => (sign && VARNA_BY_RASHI[sign]) || null;

const ARCMIN_PER_SIGN = 30 * 60;
const ARCMIN_PER_NAVAMSA = 200; // 3°20'
const ARCMIN_PER_NAKSHATRA = 800; // 13°20'
const ARCMIN_PER_PADA = 200; // 3°20'
// Absorbs float noise of deg+min/60 inputs (≈6e-6″), so exact boundaries land in the next part.
const ARCMIN_EPSILON = 1e-7;

export const normalizeLongitude = (longitude) => ((longitude % 360) + 360) % 360;

const toArcminutes = (longitude) => {
  const arcmin = normalizeLongitude(longitude) * 60 + ARCMIN_EPSILON;
  return arcmin >= 360 * 60 ? arcmin - 360 * 60 : arcmin;
};

/**
 * D9 sign by sidereal longitude. Movable signs start from themselves, fixed from the 9th,
 * dual from the 5th — which equals counting navamsas continuously from Aries.
 * @param {number} siderealLongitude - degrees
 * @returns {string|null} sign name (rashiNames)
 */
export function getNavamsaSign(siderealLongitude) {
  if (typeof siderealLongitude !== 'number' || !Number.isFinite(siderealLongitude)) return null;
  const arcmin = toArcminutes(siderealLongitude);
  const rashiIndex = Math.floor(arcmin / ARCMIN_PER_SIGN) % 12;
  const navamsaInSign = Math.floor((arcmin - rashiIndex * ARCMIN_PER_SIGN) / ARCMIN_PER_NAVAMSA);
  const startOffset = [0, 8, 4][rashiIndex % 3]; // movable, fixed, dual
  return rashiNames[(rashiIndex + startOffset + navamsaInSign) % 12];
}

const getRashiByLongitude = (longitude) => {
  if (typeof longitude !== 'number' || !Number.isFinite(longitude)) return null;
  return rashiNames[Math.floor(toArcminutes(longitude) / ARCMIN_PER_SIGN) % 12];
};

const getPadaByLongitude = (longitude) => {
  if (typeof longitude !== 'number' || !Number.isFinite(longitude)) return null;
  return Math.floor((toArcminutes(longitude) % ARCMIN_PER_NAKSHATRA) / ARCMIN_PER_PADA) + 1;
};

const pointSign = (point) => point?.rashiName || getRashiByLongitude(point?.longitude);

const moonNavamsaSign = (chart) =>
  chart?.vargas?.d9?.planets?.Moon?.rashiName || getNavamsaSign(chart?.planets?.Moon?.longitude);

const indicator = (source, chartId, sign) => ({
  source,
  chart: chartId,
  sign: sign || null,
  varna: getVarnaByRashi(sign),
});

const emptyDistribution = () => Object.fromEntries(VARNA_ORDER.map((id) => [id, 0]));

const countVarnas = (indicators) =>
  indicators.reduce((acc, one) => {
    if (one.varna) acc[one.varna] += 1;
    return acc;
  }, emptyDistribution());

/**
 * Moon D9 (inner potential) wins whenever it differs from the primary.
 * Otherwise Ascendant + Sun may set it, but only together and only if different from the primary.
 */
const pickSecondary = (primary, moonD9, ascendant, sun) => {
  if (!primary) return { secondary: null, secondarySource: null };
  if (moonD9.varna && moonD9.varna !== primary) {
    return { secondary: moonD9.varna, secondarySource: 'moon_D9' };
  }
  if (ascendant.varna && ascendant.varna === sun.varna && ascendant.varna !== primary) {
    return { secondary: ascendant.varna, secondarySource: 'ascendant_sun' };
  }
  return { secondary: null, secondarySource: null };
};

/** Ascendant + Sun as one strong pattern when they agree, otherwise each separately. */
const groupManifestation = (ascendant, sun) => {
  if (ascendant.varna && ascendant.varna === sun.varna) {
    return [{ varna: ascendant.varna, sources: ['ascendant', 'sun'], strong: true }];
  }
  return [ascendant, sun]
    .filter((one) => one.varna)
    .map((one) => ({ varna: one.varna, sources: [one.source], strong: false }));
};

/** Ascendant / Sun varnas not already covered by primary or secondary, in indicator order. */
const pickAdditional = (primary, secondary, ascendant, sun) =>
  [...new Set([ascendant.varna, sun.varna])].filter((id) => id && id !== primary && id !== secondary);

/**
 * @param {Object} chart - panchangam-js kundli (sidereal D1 + optional vargas.d9)
 * @returns {{
 *   primary: string|null,
 *   secondary: string|null,
 *   secondarySource: 'moon_D9'|'ascendant_sun'|null,
 *   additionalInfluences: string[],
 *   levels: {
 *     core: string|null,
 *     inner: string|null,
 *     manifestation: Array<{varna: string, sources: string[], strong: boolean}>,
 *   },
 *   indicators: Array<{source: string, chart: string, sign: string|null, varna: string|null}>,
 *   distribution: Record<string, number>,
 *   nakshatra: {name: string|null, pada: number|null},
 * }}
 */
export function calculateVarnaProfile(chart) {
  const moon = indicator('moon', 'D1', pointSign(chart?.planets?.Moon));
  const moonD9 = indicator('moon', 'D9', moonNavamsaSign(chart));
  const ascendant = indicator('ascendant', 'D1', pointSign(chart?.ascendant));
  const sun = indicator('sun', 'D1', pointSign(chart?.planets?.Sun));

  const indicators = [moon, moonD9, ascendant, sun];
  const primary = moon.varna;
  const { secondary, secondarySource } = pickSecondary(primary, moonD9, ascendant, sun);

  return {
    primary,
    secondary,
    secondarySource,
    additionalInfluences: primary ? pickAdditional(primary, secondary, ascendant, sun) : [],
    levels: {
      core: primary,
      inner: moonD9.varna,
      manifestation: groupManifestation(ascendant, sun),
    },
    indicators,
    distribution: countVarnas(indicators),
    // Reference only: not scored until a sourced Nakshatra Varna method is added.
    nakshatra: {
      name: chart?.dasha?.birthNakshatra || null,
      pada: getPadaByLongitude(chart?.planets?.Moon?.longitude),
    },
  };
}
